export type VideoUploadStage = "idle" | "shrinking" | "sending" | "preparing" | "error" | "ready";

export const UPLOAD_LEAVE_MESSAGE =
  "Upload in progress. If you leave, it will stop.";

export function formatFileSize(bytes: number): string {
  const size = Number(bytes);
  if (!Number.isFinite(size) || size <= 0) {
    return "";
  }
  if (size < 1024 * 1024) {
    return `${Math.max(1, Math.round(size / 1024))} KB`;
  }
  const mb = size / (1024 * 1024);
  if (mb < 10) {
    return `${mb.toFixed(1)} MB`;
  }
  return `${Math.round(mb)} MB`;
}

export function estimateUploadWait(bytes: number): string {
  const size = Number(bytes);
  if (!Number.isFinite(size) || size <= 0) {
    return "";
  }
  if (size < 20 * 1024 * 1024) {
    return "About a minute on cellular";
  }
  if (size < 80 * 1024 * 1024) {
    return "A few minutes on cellular";
  }
  if (size < 500 * 1024 * 1024) {
    return "This may take several minutes — keep the screen on";
  }
  return "This is a large file and may take a long time — keep the screen on";
}

export function shrinkingStatusText(percent: number): string {
  const pct = Math.max(0, Math.min(100, Math.trunc(Number(percent) || 0)));
  return `Keep this screen on. ${pct}%`;
}

export function sendingStatusText(percent: number, fileSizeLabel?: string): string {
  const pct = Math.max(0, Math.min(100, Math.trunc(Number(percent) || 0)));
  if (pct >= 100) {
    return "Video received. Preparing it…";
  }
  const sizeBit = fileSizeLabel ? ` · ${fileSizeLabel}` : "";
  return `Sending your video… ${pct}%${sizeBit}`;
}

/** Shown while Mux encodes. A running clock so long videos don't look stuck. */
export function processingStatusText(elapsedSeconds: number): string {
  const secs = Math.max(0, Math.trunc(Number(elapsedSeconds) || 0));
  const clock = `${Math.floor(secs / 60)}:${String(secs % 60).padStart(2, "0")}`;
  if (secs < 60) {
    return `Video received. Processing it… ${clock}`;
  }
  return `Processing your video… ${clock}. Long videos can take a few minutes. You can keep writing and submit.`;
}

export const VIDEO_ADDED_BANNER =
  "Video is still processing. Once it is processed, we will send you a mail.";

export const VIDEO_PROCESSING_TITLE = "Video is still processing";
export const VIDEO_PROCESSING_MESSAGE =
  "Once it is processed, we will send you a mail.";

export function clampUploadPercent(detail: any): number {
  const value = Number(detail);
  if (!Number.isFinite(value)) {
    return 0;
  }
  return Math.max(0, Math.min(100, Math.trunc(value)));
}

/** Every caller sends the file straight to Mux in chunks, so phone and desktop share one limit. */
export const MAX_VIDEO_UPLOAD_BYTES = 1024 * 1024 * 1024;
export const MOBILE_MAX_VIDEO_UPLOAD_BYTES = MAX_VIDEO_UPLOAD_BYTES;
export const WARN_VIDEO_UPLOAD_BYTES = 80 * 1024 * 1024;
/** Re-encode phone Camera Roll clips above this size before Mux. */
export const SHRINK_VIDEO_MIN_BYTES = WARN_VIDEO_UPLOAD_BYTES;
/** Skip the shrink when the file is already close to the in-app recording rate. */
export const SHRINK_IF_BITRATE_ABOVE = 4_000_000;
/** Same video rate as in-app 720p recording. */
export const TARGET_STORY_VIDEO_BITRATE = 1_500_000;
export const TARGET_SHORT_SIDE = 720;

export type VideoSizeGuard = {
  allowed: boolean;
  warn: boolean;
  message: string;
};

export function inspectVideoUploadSize(
  bytes: number,
  options?: { mobile?: boolean },
): VideoSizeGuard {
  const size = Number(bytes);
  if (!Number.isFinite(size) || size <= 0) {
    return { allowed: true, warn: false, message: "" };
  }
  const mobile = !!options?.mobile;
  if (size > MAX_VIDEO_UPLOAD_BYTES) {
    return {
      allowed: false,
      warn: false,
      message: mobile
        ? `This video is ${formatFileSize(size)}. The phone limit is 1 GB. Keep this screen on, or pick a shorter clip.`
        : `This video is ${formatFileSize(size)}. The limit is 1 GB. Pick a shorter clip.`,
    };
  }
  if (size > WARN_VIDEO_UPLOAD_BYTES) {
    return {
      allowed: true,
      warn: true,
      message: `This is a large file (${formatFileSize(size)}). Keep this screen on until it finishes sending.`,
    };
  }
  return { allowed: true, warn: false, message: "" };
}

export function videoBitrate(bytes: number, durationSeconds: number): number {
  const size = Number(bytes);
  const duration = Number(durationSeconds);
  if (!Number.isFinite(size) || size <= 0 || !Number.isFinite(duration) || duration <= 0) {
    return 0;
  }
  return (size * 8) / duration;
}

/** Phone Camera Roll files that are large and high-bitrate. Recorded clips stay under the bitrate gate. */
export function shouldShrinkMobileVideo(
  bytes: number,
  mobile: boolean,
  durationSeconds?: number,
): boolean {
  if (!mobile) {
    return false;
  }
  const size = Number(bytes);
  if (!Number.isFinite(size) || size <= SHRINK_VIDEO_MIN_BYTES) {
    return false;
  }
  if (durationSeconds == null || !Number.isFinite(durationSeconds) || durationSeconds <= 0) {
    return true;
  }
  return videoBitrate(size, durationSeconds) > SHRINK_IF_BITRATE_ABOVE;
}

/** Limit the short side to 720. Portrait clips stay portrait, and small frames are not scaled up. */
export function shrinkDimensionConstraint(
  width: number,
  height: number,
): { width?: number; height?: number } {
  const w = Math.round(Number(width));
  const h = Math.round(Number(height));
  if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) {
    return {};
  }
  if (Math.min(w, h) <= TARGET_SHORT_SIDE) {
    return {};
  }
  return w >= h ? { height: TARGET_SHORT_SIDE } : { width: TARGET_SHORT_SIDE };
}

type WakeLockSentinelLike = { released?: boolean; release: () => Promise<void> };

/** Keeps the phone screen on while Mux is receiving bytes. Released when the page hides. */
export class ScreenWakeLock {
  private sentinel: WakeLockSentinelLike | null = null;
  private held = false;

  async acquire(): Promise<void> {
    this.held = true;
    await this.request();
  }

  async reacquireIfHeld(): Promise<void> {
    if (!this.held) {
      return;
    }
    await this.request();
  }

  async release(): Promise<void> {
    this.held = false;
    const current = this.sentinel;
    this.sentinel = null;
    if (!current) {
      return;
    }
    try {
      await current.release();
    } catch {
      // The browser already dropped the lock when the page hid.
    }
  }

  private async request(): Promise<void> {
    const wakeLock = typeof navigator !== "undefined"
      ? (navigator as Navigator & { wakeLock?: { request: (type: "screen") => Promise<WakeLockSentinelLike> } }).wakeLock
      : undefined;
    if (!wakeLock?.request) {
      return;
    }
    if (this.sentinel && !this.sentinel.released) {
      return;
    }
    try {
      this.sentinel = await wakeLock.request("screen");
    } catch {
      this.sentinel = null;
    }
  }
}
